import { useMutation, useQuery } from '@tanstack/react-query';
import authService from '@services/auth';

/**
 * Sprint 47, story-232. One-time account setup link hooks.
 *
 * `useSetupVerify` is read-only and runs once per mount: it never retries, never refetches on
 * focus and is never stale, so an email-link scanner or a tab switch cannot cause extra calls,
 * and it never calls `complete`. The token only ever lives in component state: it is not written
 * to storage, analytics or the console, and the query key holds a fixed label, not the token.
 */
export const useSetupVerify = (token: string) =>
  useQuery({
    queryKey: ['setup-verify', 'once'],
    queryFn: () => authService.verifySetupToken(token),
    enabled: Boolean(token),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
  });

export const useSetupComplete = () =>
  useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authService.completeSetup(token, password),
  });

export const useSetupResend = () =>
  useMutation({
    mutationFn: (email: string) => authService.resendSetup(email),
  });
